import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Loading } from "@/components/Loading";
import { Eye, EyeOff } from "lucide-react";
import RecipientsField from "@/features/settings/components/RecipientsField";
import { FEATURES } from "@/config/featureFlags";
import { Notice } from "@/components/Notice";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select as UISelect,
  SelectContent,
  SelectItem,
  SelectEmpty,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { SearchableSelect, SEARCHABLE_MIN_OPTIONS } from "@/components/SearchableSelect";
import { cn } from "@/lib/utils";
import { ApiError } from "@/lib/apiClient";
import { settings as settingsApi, type SettingFieldDef, type SettingPageDef, type SettingsResponse } from "@/features/settings/api/settings.api";

const SECRET_MASK = "••••••••";

export default function SettingsPage() {
  const [data, setData] = useState<SettingsResponse | null>(null);
  const [values, setValues] = useState<Record<string, string>>({});
  const [baseline, setBaseline] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [forbidden, setForbidden] = useState(false);
  const [activeSlug, setActiveSlug] = useState("");
  const [saving, setSaving] = useState(false);

  const hydrate = useCallback((raw: SettingsResponse, isInitial: boolean) => {
    const d = FEATURES.equipment ? raw : { ...raw, pages: raw.pages.filter((p) => p.slug !== "equipment") };
    setData(d);
    const initial: Record<string, string> = {};
    for (const [key, v] of Object.entries(d.values)) initial[key] = v.value ?? "";
    setValues(initial);
    setBaseline(initial);
    if (isInitial) setActiveSlug(d.pages[0]?.slug ?? "");
  }, []);

  useEffect(() => {
    let cancelled = false;
    settingsApi
      .get()
      .then((d) => { if (!cancelled) hydrate(d, true); })
      .catch((e) => {
        if (cancelled) return;
        if (e instanceof ApiError && e.status === 403) setForbidden(true);
        else setError(e instanceof Error ? e.message : "Failed to load settings.");
      });
    return () => { cancelled = true; };
  }, [hydrate]);

  function setValue(key: string, v: string) {
    setValues((prev) => ({ ...prev, [key]: v }));
  }

  async function handleSave(page: SettingPageDef) {
    const dirty = page.sections.some((section) =>
      section.fields.some((f) => (values[f.key] ?? "") !== (baseline[f.key] ?? "")),
    );
    if (!dirty) {
      toast.info("No changes to save");
      return;
    }

    setSaving(true);
    try {
      const updates: Record<string, string> = {};
      for (const section of page.sections)
        for (const f of section.fields) {
          const v = values[f.key] ?? "";
          // A sensitive field left at the mask is untouched — don't send it,
          // or we'd overwrite the stored secret with "••••••••".
          if (f.sensitive && v === SECRET_MASK) continue;
          updates[f.key] = v;
        }

      const res = await settingsApi.save(updates);
      toast.success(`Saved ${res.updated} setting${res.updated === 1 ? "" : "s"}.`);

      hydrate(await settingsApi.get(), false);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Save failed.");
    } finally {
      setSaving(false);
    }
  }

  const activePage = data?.pages.find((p) => p.slug === activeSlug) ?? data?.pages[0];

  return (
    <div className="mx-auto max-w-4xl">
      <header className="mb-6">
        <h1 className="text-2xl font-semibold text-slate-800">Settings</h1>
        <p className="text-sm text-slate-500 mt-1">
          Portal-wide configuration. Secrets (passwords, API tokens) are
          masked once saved — leave a masked field untouched to keep it.
        </p>
      </header>

      {forbidden && (
        <Notice tone="warn">
          You don't have permission to view Settings — this screen is admin-only.
        </Notice>
      )}
      {error && !forbidden && <Notice tone="error">{error}</Notice>}
      {!forbidden && !error && data === null && (
        <Loading>Loading settings…</Loading>
      )}

      {!forbidden && !error && data !== null && !activePage && (
        <Notice tone="info">
          Settings haven't been set up yet. Once configured, the options will
          appear here for you to manage.
        </Notice>
      )}

      {!forbidden && !error && data !== null && activePage && (
        <>
          <Tabs
            value={activePage.slug}
            onValueChange={(slug) => setActiveSlug(slug)}
            className="mb-6"
          >
            <TabsList variant="underline" className="flex-wrap">
              {data.pages.map((p) => (
                <TabsTrigger key={p.slug} variant="underline" value={p.slug}>{p.label}</TabsTrigger>
              ))}
            </TabsList>
          </Tabs>

          <p className="text-sm text-slate-500 mb-4">{activePage.summary}</p>

          <div className="space-y-6">
            {activePage.sections.map((section) => (
              <Card key={section.title}>
                <CardHeader className="border-b py-3">
                  <CardTitle className="text-base font-semibold text-slate-900">{section.title}</CardTitle>
                  <CardDescription>{section.description}</CardDescription>
                </CardHeader>
                <CardContent className="pt-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                    {section.fields.map((f) => (
                      <Field
                        key={f.key}
                        field={f}
                        value={values[f.key] ?? ""}
                        onChange={(v) => setValue(f.key, v)}
                      />
                    ))}
                  </div>
                  {sectionHasKey(section, "smtp.host") && <TestSendRow kind="email" />}
                  {sectionHasKey(section, "verification.recipientEmail") && <TestSendRow kind="verification" />}
                  {sectionHasKey(section, "twilio.accountSid") && <TestSendRow kind="sms" />}
                </CardContent>
              </Card>
            ))}

            <div className="flex items-center justify-end">
              <Button type="button" disabled={saving} onClick={() => handleSave(activePage)}>
                {saving ? "Saving…" : "Save settings"}
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function Field({
  field,
  value,
  onChange,
}: {
  field: SettingFieldDef;
  value: string;
  onChange: (v: string) => void;
}) {
  const { label, type, placeholder, help, sensitive, options, wide, rows } = field;
  const [revealed, setRevealed] = useState(false);

  const [hadSecret] = useState(sensitive && value === SECRET_MASK);

  return (
    <div className={wide || type === "textarea" || type === "recipients" ? "md:col-span-2 xl:col-span-3" : ""}>
      <label className="block text-sm font-medium text-slate-700 mb-1">{label}</label>

      {type === "select" && options ? (
        options.length >= SEARCHABLE_MIN_OPTIONS ? (
          <SearchableSelect
            value={value}
            onValueChange={onChange}
            options={options.map((o) => ({ value: o, label: o }))}
            placeholder={placeholder ?? "Select"}
            searchPlaceholder={`Search ${label.toLowerCase()}…`}
          />
        ) : (
          <UISelect value={value || undefined} onValueChange={onChange}>
            <SelectTrigger>
              <SelectValue placeholder={placeholder ?? "Select"} />
            </SelectTrigger>
            <SelectContent>
              {options.length === 0 ? (
                <SelectEmpty />
              ) : (
                options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)
              )}
            </SelectContent>
          </UISelect>
        )
      ) : type === "textarea" ? (
        <Textarea
          value={value}
          placeholder={placeholder ?? undefined}
          onChange={(e) => onChange(e.target.value)}
          rows={rows ?? 6}
          className="font-mono leading-snug resize-y"
          autoComplete="off"
          spellCheck={false}
        />
      ) : type === "recipients" ? (
        <RecipientsField value={value} onChange={onChange} />
      ) : sensitive ? (
        <div className="relative">
          <Input
            type={revealed ? "text" : "password"}
            value={value}
            placeholder={hadSecret ? "•••••••• — leave blank to keep current" : (placeholder ?? undefined)}

            onFocus={() => { if (value === SECRET_MASK) onChange(""); }}
            onBlur={() => { if (hadSecret && value.trim() === "") onChange(SECRET_MASK); }}
            onChange={(e) => onChange(e.target.value)}
            className="font-mono pr-10"
            autoComplete="off"
            spellCheck={false}
          />
          <button
            type="button"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => setRevealed((r) => !r)}
            aria-label={revealed ? "Hide value" : "Show value"}
            title={revealed ? "Hide" : "Show"}
            className="absolute inset-y-0 right-0 flex items-center px-3 text-muted-foreground hover:text-foreground"
          >
            {revealed ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
          </button>
        </div>
      ) : (
        <Input
          type={type === "number" ? "number" : "text"}
          value={value}
          placeholder={placeholder ?? undefined}
          onChange={(e) => onChange(e.target.value)}
          autoComplete="off"
          spellCheck={false}
        />
      )}

      {help && <p className="text-xs text-slate-500 mt-1">{help}</p>}
    </div>
  );
}

function sectionHasKey(
  section: SettingPageDef["sections"][number],
  key: string,
): boolean {
  return section.fields.some((f) => f.key === key);
}

function TestSendRow({ kind }: { kind: "email" | "sms" | "verification" }) {
  const [to, setTo] = useState("");
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState<{ kind: "ok" | "err"; msg: string } | null>(null);

  const isEmail = kind === "email";
  const isVerification = kind === "verification";
  const isSms = kind === "sms";
  const label = isSms
    ? "Send test SMS"
    : isVerification
      ? "Send test verification email"
      : "Send test email";
  const placeholder = isSms
    ? "+15551234567"
    : isVerification
      ? "(optional) override recipient — blank uses the boarding address above"
      : "recipient@example.com";
  const help = isSms
    ? "Uses the saved Twilio settings to send a one-off SMS. Must be E.164."
    : isVerification
      ? "Sends From the verification address to the boarding recipient above (using the saved SMTP transport). Confirms the From mailbox can authenticate."
      : "Uses the saved SMTP settings to send a one-off message.";

  async function send() {
    setSending(true);
    setResult(null);
    try {
      const recipient = to.trim();
      if (!recipient && !isVerification) {
        setResult({ kind: "err", msg: "Enter a recipient first." });
        return;
      }

      if (isVerification) {
        const res = await settingsApi.testVerificationEmail(recipient || undefined);
        setResult(
          res.sent
            ? { kind: "ok", msg: recipient ? `Verification email sent to ${recipient}.` : "Verification email sent." }
            : { kind: "err", msg: "Send returned no confirmation." },
        );
        return;
      }

      const res = isEmail
        ? await settingsApi.testEmail(recipient)
        : await settingsApi.testSms(recipient);
      if (res.sent) {
        setResult({
          kind: "ok",
          msg: isEmail ? `Sent. Check ${recipient}'s inbox.` : `Sent. Check ${recipient}.`,
        });
      } else {
        setResult({ kind: "err", msg: "Send returned no confirmation." });
      }
    } catch (e) {
      setResult({ kind: "err", msg: e instanceof Error ? e.message : "Send failed." });
    } finally {
      setSending(false);
    }
  }

  return (
    <div className="mt-4 pt-4 border-t border-slate-200">
      <label className="block text-sm font-medium text-slate-700 mb-1">{label}</label>
      <div className="flex flex-col sm:flex-row sm:items-center gap-2">
        <Input
          type={isSms ? "tel" : "email"}
          value={to}
          onChange={(e) => setTo(e.target.value)}
          placeholder={placeholder}
          className="flex-1"
          autoComplete="off"
          spellCheck={false}
        />
        <Button type="button" variant="outline" disabled={sending} onClick={send} className="whitespace-nowrap">
          {sending ? "Sending…" : "Send test"}
        </Button>
      </div>
      <p className="text-xs text-slate-500 mt-1">{help}</p>
      {result && (
        <p className={cn("text-sm mt-2", result.kind === "ok" ? "text-emerald-700" : "text-destructive")}>
          {result.msg}
        </p>
      )}
    </div>
  );
}
